import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User, Calendar, Clock, Scissors, CheckCircle2 } from 'lucide-react';
type Message = {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  options?: string[];
};
type ReservationData = {
  service: string;
  day: string;
  time: string;
};
const BOT_AVATAR = 'https://cdn.magicpatterns.com/uploads/pyNXxmBJRcAmHgtnuqCbgP/WhatsApp_Image_2026-06-30_at_21.13.45.jpg';
export function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [step, setStep] = useState<'init' | 'service' | 'day' | 'time' | 'confirm' | 'done'>('init');
  const [reservation, setReservation] = useState<ReservationData>({
    service: '',
    day: '',
    time: ''
  });
  const [showWelcome, setShowWelcome] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Proactive welcome bubble shortly after the visitor lands on the site
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage('¡Hola! Soy el asistente virtual de Okrim Barber. 💈\n¿En qué te puedo ayudar hoy?', ['Agendar turno', 'Ver precios', 'Ubicación']);
    }
  }, [isOpen]);
  const addBotMessage = (text: string, options?: string[], delay = 600) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        sender: 'bot',
        text,
        options
      }]);
      setIsTyping(false);
    }, delay);
  };
  const addUserMessage = (text: string) => {
    setMessages(prev => {
      // Remove options from the last bot message so they can't be clicked again
      const newMessages = [...prev];
      if (newMessages.length > 0 && newMessages[newMessages.length - 1].sender === 'bot') {
        newMessages[newMessages.length - 1].options = undefined;
      }
      return [...newMessages, {
        id: Date.now().toString(),
        sender: 'user',
        text
      }];
    });
  };
  const handleOptionClick = (option: string) => {
    addUserMessage(option);
    // State Machine Logic
    if (step === 'init') {
      if (option === 'Agendar turno') {
        setStep('service');
        addBotMessage('Excelente. ¿Qué servicio te gustaría hacerte hoy?', ['Corte Clásico', 'Fade / Degradé', 'Corte y Barba']);
      } else if (option === 'Ver precios') {
        addBotMessage('Nuestros precios:\n• Corte Clásico: $400\n• Fade / Degradé: $500\n• Corte y Barba: $550\n\n(Cejas incluidas en cualquier servicio)\n\n¿Querés agendar un turno?', ['Agendar turno', 'En otro momento']);
      } else if (option === 'Ubicación') {
        addBotMessage('Estamos en el Centro de Montevideo. ¡A pasos de todo!\n¿Te gustaría agendar?', ['Agendar turno', 'En otro momento']);
      } else {
        addBotMessage('¡Dale! Te esperamos cuando gustes. Podés cerrar el chat o volver a empezar.', ['Agendar turno']);
      }
    } else if (step === 'service') {
      setReservation(prev => ({
        ...prev,
        service: option
      }));
      setStep('day');
      addBotMessage('¡Buenísimo! ¿Para qué día estás buscando?', ['Hoy', 'Mañana', 'Esta semana', 'La semana que viene']);
    } else if (step === 'day') {
      setReservation(prev => ({
        ...prev,
        day: option
      }));
      setStep('time');
      addBotMessage('Perfecto. ¿En qué franja horaria te queda mejor?', ['Mañana (10:00 - 13:00)', 'Tarde (14:00 - 17:00)', 'Noche (17:00 - 20:00)']);
    } else if (step === 'time') {
      const newRes = {
        ...reservation,
        time: option
      };
      setReservation(newRes);
      setStep('confirm');
      addBotMessage(`¡Ya casi estamos! Resumen de tu solicitud:\n\n✂️ Servicio: ${newRes.service}\n📅 Día: ${newRes.day}\n⏰ Horario: ${newRes.time}\n\nPara confirmar, te voy a derivar a WhatsApp con todo listo. ¿Avanzamos?`, ['Sí, confirmar por WhatsApp', 'Cancelar']);
    } else if (step === 'confirm') {
      if (option === 'Sí, confirmar por WhatsApp') {
        setStep('done');
        addBotMessage('¡Genial! Abriendo WhatsApp... Te esperamos en Okrim Barber. 💈');
        // Build WhatsApp URL
        const text = `Hola Okrim! Quería agendar un turno.\n\n*Servicio:* ${reservation.service}\n*Día:* ${reservation.day}\n*Preferencia:* ${reservation.time}\n\n¿Tienen disponibilidad?`;
        const waUrl = `https://wa.me/59892738664?text=${encodeURIComponent(text)}`;
        setTimeout(() => {
          window.open(waUrl, '_blank');
        }, 1500);
      } else {
        setStep('init');
        addBotMessage('No hay problema. ¿Te puedo ayudar con algo más?', ['Agendar turno', 'Ver precios']);
      }
    }
  };
  const openChat = () => {
    setShowWelcome(false);
    setIsOpen(true);
  };
  const closeChat = () => {
    setIsOpen(false);
    // Reset the conversation so the assistant greets fresh next time
    setMessages([]);
    setStep('init');
    setReservation({
      service: '',
      day: '',
      time: ''
    });
    setIsTyping(false);
  };
  return <>
      {/* Proactive welcome bubble */}
      <AnimatePresence data-id="element-31">
        {showWelcome && !isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.9
      }} transition={{
        type: 'spring',
        damping: 20,
        stiffness: 200
      }} className="fixed bottom-24 right-4 sm:right-6 z-50 w-64" data-id="element-32">
            <div className="relative glass-panel rounded-2xl rounded-br-sm p-4 shadow-2xl shadow-red-900/30" data-id="element-33">
              <button onClick={() => setShowWelcome(false)} aria-label="Cerrar mensaje de bienvenida" className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-zinc-100 transition-colors" data-id="element-34">
                <X size={14} data-id="element-35" />
              </button>
              <div className="flex items-start gap-3" data-id="element-36">
                <span className="text-2xl leading-none" data-id="element-37">💈</span>
                <p className="text-sm text-zinc-200 leading-snug" data-id="element-38">
                  ¡Bienvenido a{' '}
                  <span className="text-red-400 font-semibold" data-id="element-39">
                    Okrim Barber
                  </span>
                  ! ¿Reservamos tu turno?
                </p>
              </div>
              <button onClick={openChat} className="mt-3 w-full py-2 bg-red-600 hover:bg-red-500 text-zinc-50 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors" data-id="element-40">
                Empezar
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      delay: 1,
      type: 'spring'
    }} onClick={openChat} className={`fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-2xl shadow-red-500/20 transition-all duration-300 hover:scale-110 ${isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'bg-zinc-900 border border-red-500/30 text-zinc-50'}`} data-id="element-41">
        <span className="text-2xl leading-none" data-id="element-42">💈</span>
        <span className="absolute -top-2 -right-2 flex h-4 w-4" data-id="element-43">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-44"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-zinc-950" data-id="element-45"></span>
        </span>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence data-id="element-46">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-50 w-auto sm:w-[400px] h-[75dvh] sm:h-[600px] max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] glass-panel rounded-2xl flex flex-col overflow-hidden" data-id="element-47">
            {/* Header */}
            <div className="bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between" data-id="element-48">
              <div className="flex items-center gap-3" data-id="element-49">
                <div className="relative" data-id="element-50">
                  <img src={BOT_AVATAR} alt="Okrim Barber" className="w-10 h-10 rounded-full object-cover border border-red-500/40" data-id="element-51" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-zinc-900" data-id="element-52"></div>
                </div>
                <div data-id="element-53">
                  <h3 className="font-medium text-zinc-100" data-id="element-54">Okrim Assistant</h3>
                  <p className="text-xs text-zinc-400" data-id="element-55">En línea</p>
                </div>
              </div>
              <button onClick={closeChat} className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-full transition-colors" data-id="element-56">
                <X size={20} data-id="element-57" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent" data-id="element-58">
              {messages.map(msg => <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-59">
                  <div className={`flex items-end gap-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-60">
                    {msg.sender === 'user' ? <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-zinc-800 text-zinc-300" data-id="element-61">
                        <User size={16} data-id="element-62" />
                      </div> : <img src={BOT_AVATAR} alt="Okrim Barber" className="w-8 h-8 rounded-full flex-shrink-0 object-cover border border-red-500/30" data-id="element-63" />}
                    <div className={`px-4 py-3 rounded-2xl whitespace-pre-wrap text-[15px] leading-relaxed ${msg.sender === 'user' ? 'bg-red-600 text-zinc-50 rounded-br-sm' : 'bg-zinc-800 text-zinc-100 rounded-bl-sm border border-zinc-700/50'}`} data-id="element-64">
                      {msg.text}
                    </div>
                  </div>

                  {/* Options */}
                  {msg.options && <div className="flex flex-wrap gap-2 mt-3 ml-10" data-id="element-65">
                      {msg.options.map((opt, idx) => <motion.button initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: idx * 0.1
              }} key={idx} onClick={() => handleOptionClick(opt)} className="px-4 py-2.5 bg-zinc-900 border border-red-500/30 text-red-400 text-sm font-medium rounded-full hover:bg-red-600 hover:text-zinc-50 active:scale-95 transition-all" data-id="element-66">
                          {opt}
                        </motion.button>)}
                    </div>}
                </motion.div>)}

              {isTyping && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex items-end gap-2" data-id="element-67">
                  <img src={BOT_AVATAR} alt="Okrim Barber" className="w-8 h-8 rounded-full object-cover border border-red-500/30" data-id="element-68" />
                  <div className="bg-zinc-800 p-4 rounded-2xl rounded-bl-sm flex gap-1.5" data-id="element-69">
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-70"></span>
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-71"></span>
                    <span className="w-1.5 h-1.5 bg-zinc-400 rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-72"></span>
                  </div>
                </motion.div>}
              <div ref={messagesEndRef} data-id="element-73" />
            </div>

            {/* Input Area (Disabled for guided flow, just visual) */}
            <div className="p-4 bg-zinc-900 border-t border-zinc-800" data-id="element-74">
              <div className="relative flex items-center" data-id="element-75">
                <input type="text" disabled placeholder="Seleccioná una opción arriba..." className="w-full bg-zinc-950 border border-zinc-800 rounded-full py-3 pl-4 pr-12 text-sm text-zinc-400 focus:outline-none cursor-not-allowed" data-id="element-76" />
                <button disabled className="absolute right-2 p-2 text-zinc-600" data-id="element-77">
                  <Send size={18} data-id="element-78" />
                </button>
              </div>
              <div className="text-center mt-2" data-id="element-79">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium" data-id="element-80">
                  Asistente de Reservas Inteligente
                </span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}